import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  fetchContacts,
  fetchStats,
  fetchChatAnalytics,
  updateContactStatus,
  deleteContact,
  exportContactsCsvUrl,
  fetchContactNotes,
  addContactNote,
  deleteContactNote,
} from "../lib/api";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Mail,
  Phone,
  Building,
  Trash2,
  Bot,
  Globe,
  TrendingUp,
  Moon,
  Download,
  FileText,
  StickyNote,
} from "lucide-react";

const STATUS_OPTIONS = ["new", "in_progress", "resolved", "archived"];
const SOURCE_OPTIONS = ["all", "web", "chatbot"];
const WINDOW_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "30d", label: "Last 30 days" },
  { value: "7d", label: "Last 7 days" },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsWindow, setAnalyticsWindow] = useState("all");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [notes, setNotes] = useState([]);
  const [noteDraft, setNoteDraft] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [c, s, a] = await Promise.all([
        fetchContacts(),
        fetchStats(),
        fetchChatAnalytics(analyticsWindow),
      ]);
      setContacts(c);
      setStats(s);
      setAnalytics(a);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("gr_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  // Re-fetch analytics when window changes
  useEffect(() => {
    if (!localStorage.getItem("gr_admin_token")) return;
    fetchChatAnalytics(analyticsWindow).then(setAnalytics).catch(() => {});
  }, [analyticsWindow]);

  // Load notes when a contact is selected
  useEffect(() => {
    if (!selected) {
      setNotes([]);
      return;
    }
    fetchContactNotes(selected.id).then(setNotes).catch(() => setNotes([]));
  }, [selected]);

  useEffect(() => {
    if (!localStorage.getItem("gr_admin_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("gr_admin_token");
    localStorage.removeItem("gr_admin_email");
    navigate("/admin/login");
  };

  const onStatusChange = async (id, status) => {
    try {
      await updateContactStatus(id, status);
      toast.success("Status updated");
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Update failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await deleteContact(id);
      toast.success("Inquiry deleted");
      if (selected?.id === id) setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const onExportCsv = async () => {
    const { url, token } = exportContactsCsvUrl(sourceFilter === "all" ? null : sourceFilter);
    try {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const dispo = res.headers.get("Content-Disposition") || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      const filename = m ? m[1] : "greenroute-inquiries.csv";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("CSV downloaded");
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const onAddNote = async () => {
    const body = noteDraft.trim();
    if (!body || !selected) return;
    setSavingNote(true);
    try {
      await addContactNote(selected.id, body);
      const updated = await fetchContactNotes(selected.id);
      setNotes(updated);
      setNoteDraft("");
      toast.success("Note added");
    } catch {
      toast.error("Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  const onDeleteNote = async (noteId) => {
    if (!selected) return;
    try {
      await deleteContactNote(selected.id, noteId);
      setNotes(notes.filter((n) => n.id !== noteId));
    } catch {
      toast.error("Failed to delete note");
    }
  };

  const filtered = contacts.filter((c) => {
    if (filter !== "all" && c.status !== filter) return false;
    if (sourceFilter !== "all") {
      const src = c.source || "web";
      if (src !== sourceFilter) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-page">
      {/* Top bar */}
      <header className="bg-forest text-cream">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-cream flex items-center justify-center">
              <span className="text-forest font-serif text-xl leading-none">G</span>
            </div>
            <div>
              <div className="font-serif text-xl">Greenroute Admin</div>
              <div className="uppercase text-[10px] tracking-[0.25em] text-cream/70">
                Control Room
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/admin/blog")}
              className="text-sm text-cream/85 hover:text-cream flex items-center gap-2"
              data-testid="open-blog-admin"
            >
              <FileText size={14} /> Blog
            </button>
            <button
              onClick={onExportCsv}
              className="text-sm text-cream/85 hover:text-cream flex items-center gap-2"
              data-testid="export-csv-btn"
            >
              <Download size={14} /> Export CSV
            </button>
            <button
              onClick={load}
              className="text-sm text-cream/85 hover:text-cream flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw size={14} /> Refresh
            </button>
            <button
              onClick={logout}
              className="text-sm bg-cream text-forest px-4 py-2 hover:bg-gold hover:text-cream transition-colors"
              data-testid="logout-btn"
            >
              <LogOut size={14} className="inline mr-1" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6" data-testid="stats-row">
          {[
            { l: "Total Inquiries", v: stats?.total_contacts ?? "—" },
            { l: "New", v: stats?.new_contacts ?? "—", accent: true },
            { l: "In Progress", v: stats?.in_progress_contacts ?? "—" },
            { l: "Resolved", v: stats?.resolved_contacts ?? "—" },
            { l: "Chat Sessions", v: stats?.chat_sessions ?? "—" },
            { l: "Chat Messages", v: stats?.chat_messages ?? "—" },
          ].map((s) => (
            <div
              key={s.l}
              className={`bg-white border border-bordersoft p-5 ${s.accent ? "border-l-4 border-l-gold" : ""}`}
            >
              <div className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2">
                {s.l}
              </div>
              <div className="font-serif text-3xl text-forest">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Chatbot performance */}
        <section className="bg-forest text-cream mb-10" data-testid="chat-analytics">
          <div className="px-6 md:px-8 py-5 flex items-center justify-between border-b border-cream/15 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Bot size={18} className="text-gold" />
              <h2 className="font-serif text-xl">Chatbot performance</h2>
            </div>
            <div className="flex gap-2" data-testid="analytics-window-tabs">
              {WINDOW_OPTIONS.map((w) => (
                <button
                  key={w.value}
                  onClick={() => setAnalyticsWindow(w.value)}
                  data-testid={`analytics-window-${w.value}`}
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                    analyticsWindow === w.value
                      ? "bg-cream text-forest border-cream"
                      : "bg-transparent text-cream/70 border-cream/30 hover:border-cream"
                  }`}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 divide-x divide-cream/10">
            {[
              {
                l: "Sessions started",
                v: analytics?.sessions_started ?? "—",
              },
              {
                l: "Quotes submitted",
                v: analytics?.quotes_submitted ?? "—",
                accent: true,
              },
              {
                l: "Conversion",
                v: analytics
                  ? `${(analytics.conversion_rate * 100).toFixed(1)}%`
                  : "—",
                icon: TrendingUp,
              },
              {
                l: "Avg turns to quote",
                v: analytics?.avg_turns_to_quote ?? "—",
              },
              {
                l: "After-hours share",
                v: analytics
                  ? `${(analytics.after_hours_share * 100).toFixed(0)}%`
                  : "—",
                icon: Moon,
              },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.l} className="p-5 md:p-6">
                  <div className="flex items-center gap-2 mb-2">
                    {Icon && <Icon size={12} className="text-gold" />}
                    <div className="uppercase text-[10px] tracking-[0.25em] text-cream/60">
                      {s.l}
                    </div>
                  </div>
                  <div
                    className={`font-serif text-3xl ${
                      s.accent ? "text-gold" : "text-cream"
                    }`}
                  >
                    {s.v}
                  </div>
                </div>
              );
            })}
          </div>
          {/* Top services */}
          {analytics?.top_services?.length > 0 && (
            <div className="px-6 md:px-8 py-5 border-t border-cream/10" data-testid="top-services">
              <div className="uppercase text-[10px] tracking-[0.25em] text-cream/60 mb-3">
                Top services via chatbot
              </div>
              <div className="flex flex-wrap gap-2">
                {analytics.top_services.map((s) => (
                  <span
                    key={s.service}
                    className="inline-flex items-center gap-2 px-3 py-1.5 border border-cream/20 text-sm"
                    data-testid={`top-service-${s.service.replace(/\W/g, "-").toLowerCase()}`}
                  >
                    {s.service}
                    <span className="text-gold font-medium">{s.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Inquiry list */}
          <section className="lg:col-span-7 bg-white border border-bordersoft">
            <div className="border-b border-bordersoft px-6 py-4 flex flex-col gap-3">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <h2 className="font-serif text-2xl text-forest">Inquiries</h2>
                <div className="flex flex-wrap gap-2">
                  {["all", ...STATUS_OPTIONS].map((s) => (
                    <button
                      key={s}
                      onClick={() => setFilter(s)}
                      data-testid={`filter-${s}`}
                      className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                        filter === s
                          ? "bg-forest text-cream border-forest"
                          : "bg-cream text-graphite border-bordersoft hover:border-forest"
                      }`}
                    >
                      {s.replace("_", " ")}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="uppercase text-[10px] tracking-[0.25em] text-sage mr-1">
                  Source:
                </span>
                {SOURCE_OPTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSourceFilter(s)}
                    data-testid={`source-filter-${s}`}
                    className={`inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                      sourceFilter === s
                        ? "bg-forest text-cream border-forest"
                        : "bg-cream text-graphite border-bordersoft hover:border-forest"
                    }`}
                  >
                    {s === "chatbot" ? <Bot size={11} /> : s === "web" ? <Globe size={11} /> : null}
                    {s === "web" ? "Web Form" : s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>

            <div className="divide-y divide-bordersoft max-h-[640px] overflow-y-auto" data-testid="inquiries-list">
              {loading && <div className="p-8 text-graphite text-sm">Loading…</div>}
              {!loading && filtered.length === 0 && (
                <div className="p-8 text-graphite text-sm">No inquiries.</div>
              )}
              {filtered.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setSelected(c)}
                  data-testid={`inquiry-row-${c.id}`}
                  className={`w-full text-left px-6 py-5 hover:bg-cream transition-colors ${
                    selected?.id === c.id ? "bg-cream" : ""
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-serif text-xl text-ink leading-tight">{c.name}</div>
                        {c.source === "chatbot" && (
                          <span
                            className="inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.2em] px-1.5 py-0.5 bg-forest text-cream"
                            data-testid="source-badge-chatbot"
                          >
                            <Bot size={9} /> Chatbot
                          </span>
                        )}
                      </div>
                      <div className="text-graphite text-sm mt-1">{c.email}</div>
                      {c.service && (
                        <div className="text-xs text-sage uppercase tracking-[0.2em] mt-2">
                          {c.service}
                        </div>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <span
                        className={`inline-block text-[10px] uppercase tracking-[0.2em] px-2 py-1 ${
                          c.status === "new"
                            ? "bg-gold text-forest"
                            : c.status === "in_progress"
                            ? "bg-sage text-cream"
                            : c.status === "resolved"
                            ? "bg-forest text-cream"
                            : "bg-bordersoft text-graphite"
                        }`}
                      >
                        {c.status.replace("_", " ")}
                      </span>
                      <div className="text-[10px] text-graphite mt-2">
                        {new Date(c.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>

          {/* Detail */}
          <aside className="lg:col-span-5">
            {selected ? (
              <div className="bg-white border border-bordersoft p-6 sticky top-6" data-testid="inquiry-detail">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="font-serif text-3xl text-forest leading-tight">{selected.name}</h3>
                    <div className="uppercase text-[10px] tracking-[0.25em] text-sage mt-2">
                      Submitted {new Date(selected.created_at).toLocaleString()}
                    </div>
                  </div>
                  <button
                    onClick={() => onDelete(selected.id)}
                    className="text-graphite hover:text-red-600 transition-colors"
                    data-testid="delete-inquiry-btn"
                    aria-label="Delete inquiry"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>

                <div className="space-y-3 text-sm mb-6">
                  <div className="flex items-center gap-3">
                    <Mail size={14} className="text-sage" />
                    <a href={`mailto:${selected.email}`} className="text-ink link-underline">
                      {selected.email}
                    </a>
                  </div>
                  {selected.phone && (
                    <div className="flex items-center gap-3">
                      <Phone size={14} className="text-sage" />
                      <a href={`tel:${selected.phone}`} className="text-ink link-underline">
                        {selected.phone}
                      </a>
                    </div>
                  )}
                  {selected.company && (
                    <div className="flex items-center gap-3">
                      <Building size={14} className="text-sage" />
                      <span className="text-ink">{selected.company}</span>
                    </div>
                  )}
                </div>

                {selected.service && (
                  <div className="mb-6">
                    <div className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2">
                      Service interest
                    </div>
                    <div className="text-ink">{selected.service}</div>
                  </div>
                )}

                <div className="mb-6">
                  <div className="uppercase text-[10px] tracking-[0.25em] text-sage mb-2">
                    Message
                  </div>
                  <p className="text-ink leading-relaxed whitespace-pre-wrap text-sm">
                    {selected.message}
                  </p>
                </div>

                <div>
                  <div className="uppercase text-[10px] tracking-[0.25em] text-sage mb-3">
                    Update status
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((s) => (
                      <button
                        key={s}
                        onClick={() => onStatusChange(selected.id, s)}
                        data-testid={`status-btn-${s}`}
                        className={`text-[10px] uppercase tracking-[0.2em] px-3 py-2 border transition-colors ${
                          selected.status === s
                            ? "bg-forest text-cream border-forest"
                            : "bg-cream text-graphite border-bordersoft hover:border-forest"
                        }`}
                      >
                        {s.replace("_", " ")}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Internal notes */}
                <div className="mt-8 pt-6 border-t border-bordersoft" data-testid="notes-section">
                  <div className="flex items-center gap-2 mb-4">
                    <StickyNote size={14} className="text-sage" />
                    <div className="uppercase text-[10px] tracking-[0.25em] text-sage">
                      Internal notes
                    </div>
                    <span className="text-[10px] text-graphite/60">
                      (feeds the chatbot's organisational memory)
                    </span>
                  </div>

                  <div className="space-y-3 mb-4">
                    {notes.length === 0 ? (
                      <div className="text-xs text-graphite/70">No notes yet.</div>
                    ) : (
                      notes.map((n) => (
                        <div
                          key={n.id}
                          className="bg-cream border border-bordersoft p-3 flex items-start justify-between gap-3"
                          data-testid={`note-${n.id}`}
                        >
                          <div className="flex-1">
                            <div className="text-sm text-ink leading-relaxed whitespace-pre-wrap">
                              {n.body}
                            </div>
                            <div className="text-[10px] text-graphite mt-2">
                              {n.author} · {new Date(n.created_at).toLocaleString()}
                            </div>
                          </div>
                          <button
                            onClick={() => onDeleteNote(n.id)}
                            className="text-graphite hover:text-red-600 transition shrink-0"
                            data-testid={`delete-note-${n.id}`}
                            aria-label="Delete note"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))
                    )}
                  </div>

                  <div className="flex gap-2">
                    <textarea
                      value={noteDraft}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      placeholder="Add an internal note (e.g. 'Quoted R8,200 — client agreed verbally, awaiting written go-ahead.')"
                      rows={2}
                      data-testid="note-draft-input"
                      className="flex-1 bg-cream border border-bordersoft px-3 py-2 text-sm text-ink focus:outline-none focus:border-forest resize-none"
                    />
                    <button
                      onClick={onAddNote}
                      disabled={savingNote || !noteDraft.trim()}
                      data-testid="add-note-btn"
                      className="bg-forest text-cream px-4 text-xs uppercase tracking-[0.2em] hover:bg-forest/90 transition disabled:opacity-50"
                    >
                      {savingNote ? "…" : "Add"}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-bordersoft p-10 text-center text-graphite text-sm">
                Select an inquiry to view details.
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
